import {
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { CURRENCIES, type Currency } from '@vy/types';
import { values, vyDate } from '@vy/utils';
import React, { useMemo } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { VyIcon } from '@vy/ui-design-system';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import type { ExchangeRateFormValues } from '../dynamic-invoice-header-types/exchange-rate-form-schema';

type ExchangeRateDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  contractCurrency: Currency | undefined;
  rateDate?: Date;
  form: UseFormReturn<ExchangeRateFormValues>;
  placeholderExchangeRate: number;
  onCurrencyChange: (currency: Currency) => void;
  onResetToOriginal: () => void;
};

const allCurrencyOptions = values(CURRENCIES);

export const ExchangeRateDialog: React.FC<ExchangeRateDialogProps> = ({
  open,
  onClose,
  onSubmit,
  contractCurrency,
  rateDate,
  form,
  placeholderExchangeRate,
  onCurrencyChange,
  onResetToOriginal,
}) => {
  const watchedInvoiceCurrency = form.watch('invoiceCurrency');
  const isSameCurrency = watchedInvoiceCurrency === contractCurrency;
  const currencyOptions = useMemo(
    () => allCurrencyOptions.filter((currencyOption) => currencyOption !== contractCurrency),
    [contractCurrency]
  );

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Set invoice currency and rate"
      labelDescription="Override the invoice currency for this invoice only"
      ctaText="Confirm"
      ctaDisabled={!watchedInvoiceCurrency}
      cancelButtonText="Cancel"
      onSubmit={onSubmit}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        {/* Info row */}
        <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
          <Typography variant="body2" color="textSecondary">
            <strong>Default currency:</strong> {contractCurrency}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Rate date:</strong> {vyDate(rateDate).format('MMM D, YY')}
          </Typography>
        </Stack>

        {/* Form fields row */}
        <Stack direction="row" spacing={3} sx={{
          alignItems: "flex-end"
        }}>
          {/* Invoice Currency Field */}
          <Box sx={{ width: '180px' }}>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                marginBottom: '8px',
              }}
            >
              Invoice Currency
            </Typography>
            <FormControl fullWidth>
              <Controller
                name="invoiceCurrency"
                control={form.control}
                render={({ field }) => (
                  <Select
                    {...field}
                    {...getSelectorAttribute(dynamicInvoicePageSelectors.header.exchangeRateCurrencySelect)}
                    onChange={(event) => onCurrencyChange(event.target.value as Currency)}
                    sx={{
                      borderRadius: '8px',
                      height: '48px',
                      '& .MuiSelect-select': {
                        padding: '12px 16px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                  >
                    {currencyOptions.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          {/* Exchange Rate Field */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'text.secondary',
                marginBottom: '8px',
              }}
            >
              Exchange rate
            </Typography>
            <Stack direction="row" spacing={1} sx={{
              alignItems: "center"
            }}>
              <Controller
                name="exchangeRate"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    {...getSelectorAttribute(dynamicInvoicePageSelectors.header.exchangeRateInput)}
                    onChange={(event) => {
                      const val = event.target.value;
                      field.onChange(val === '' ? null : Number(val));
                    }}
                    disabled={isSameCurrency}
                    placeholder={String(placeholderExchangeRate)}
                    type="number"
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': {
                        backgroundColor: 'grey.10',
                        borderRadius: '8px',
                        height: '48px',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                              }}
                            >
                              1 {contractCurrency}=
                            </Typography>
                          </InputAdornment>
                        ),
                      },

                      htmlInput: {
                        step: 0.001,
                        min: 0,
                      }
                    }} />
                )}
              />
              <Typography sx={{ color: 'text.secondary', fontSize: '14px', minWidth: '40px' }}>
                {watchedInvoiceCurrency || ''}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        {/* Reset link */}
        <Box>
          <Button
            {...getSelectorAttribute(dynamicInvoicePageSelectors.header.exchangeRateResetButton)}
            variant="text"
            onClick={onResetToOriginal}
            startIcon={<VyIcon icon="Undo" />}
            sx={{
              textTransform: 'none',
              padding: 0,
              minWidth: 'auto',
              color: 'text.secondary',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              },
            }}
          >
            Reset to original rate
          </Button>
        </Box>
      </Stack>
    </VyDialog>
  );
};
